import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import MovieCard from "@/components/base/MovieCard";
import Breadcrumb from "@/components/base/Breadcrumb";
import EmptyState from "@/components/base/EmptyState";
import Pagination from "@/components/base/Pagination";
import { filterMovies } from "@/lib/catalog";
import { yearOptions } from "@/mocks/movies";
import { useSeo } from "@/hooks/useSeo";

const PAGE_SIZE = 6;

const typeOptions = ["全部", "电影", "电视剧"];
const regionOptions = ["全部", "国产", "海外"];
const yearFilters = ["全部", ...yearOptions.map(String), "2023及以前"];
const sortOptions = [
  { key: "updated", label: "最近更新" },
  { key: "score", label: "评分最高" },
  { key: "popular", label: "热门度" },
];

export default function Category() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [filterOpen, setFilterOpen] = useState(false);

  const keyword = searchParams.get("q") ?? "";
  const tag = searchParams.get("tag") ?? "";
  const typeParam = searchParams.get("type") ?? "";
  const regionParam = searchParams.get("region") ?? "";
  const yearParam = searchParams.get("year") ?? "";
  const sortParam = searchParams.get("sort") ?? "updated";
  const pageParam = Number(searchParams.get("page") ?? "1") || 1;

  const effectiveType =
    typeParam || (tag === "电影" || tag === "电视剧" ? tag : "") || "全部";
  const effectiveRegion =
    regionParam || (tag === "国产" || tag === "海外" ? tag : "") || "全部";
  const effectiveSort = sortParam || (tag === "热门推荐" ? "popular" : "updated");

  const results = useMemo(
    () =>
      filterMovies({
        keyword,
        type: effectiveType,
        region: effectiveRegion,
        year: yearParam,
        sort: effectiveSort,
      }),
    [keyword, effectiveType, effectiveRegion, yearParam, effectiveSort]
  );

  const totalPages = Math.max(1, Math.ceil(results.length / PAGE_SIZE));
  const currentPage = Math.min(pageParam, totalPages);
  const visible = results.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);
  const hasFilters =
    Boolean(keyword) || effectiveType !== "全部" || effectiveRegion !== "全部" || Boolean(yearParam) || Boolean(tag);

  useSeo({
    title: keyword ? `${keyword} - 搜索结果 - 姐姐的朋友` : "影视分类 - 姐姐的朋友",
    description: keyword
      ? `姐姐的朋友影视搜索「${keyword}」的结果，可按类型、地区、年份多维度筛选，快速查找相关影视作品与剧情介绍。`
      : "姐姐的朋友影视分类页，支持按电影/电视剧、国产/海外、上映年份多维度筛选，并提供更新时间、评分与热门度排序，方便查找影视作品。",
    keywords: "影视分类,电影,电视剧,国产剧,海外剧,影视筛选,姐姐的朋友",
  });

  const updateParam = (key: string, value: string) => {
    const next = new URLSearchParams(searchParams);
    if (!value || value === "全部") {
      next.delete(key);
    } else {
      next.set(key, value);
    }
    next.delete("tag");
    next.delete("page");
    setSearchParams(next);
  };

  const resetAll = () => setSearchParams({});

  const goPage = (page: number) => {
    const next = new URLSearchParams(searchParams);
    if (page <= 1) next.delete("page");
    else next.set("page", String(page));
    setSearchParams(next);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const filterRowClass =
    "flex flex-col gap-2 border-b border-background-200 py-3 last:border-b-0 sm:flex-row sm:items-center sm:gap-4";

  const chipClass = (active: boolean) =>
    [
      "cursor-pointer whitespace-nowrap rounded-md px-3 py-1.5 text-xs transition-colors",
      active
        ? "bg-primary-500 text-background-50"
        : "bg-background-100 text-foreground-600 hover:bg-primary-100 hover:text-primary-700",
    ].join(" ");

  return (
    <div className="min-h-screen bg-background-100">
      <SiteHeader />
      <main className="mx-auto w-full max-w-[1400px] px-4 py-6 md:px-6 md:py-9">
        <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "影视分类" }]} />

        <div className="mt-4 flex flex-col justify-between gap-4 border-b border-background-300 pb-6 md:flex-row md:items-end">
          <div>
            <h1 className="text-2xl font-bold text-foreground-950 md:text-3xl">影视分类</h1>
            <p className="mt-2 max-w-2xl text-sm leading-6 text-foreground-600">
              按类型、地区与年份筛选作品，支持评分与热度排序。所有剧情简介与详情页保持同步。
            </p>
          </div>
          <div className="flex items-center gap-4 font-label text-xs text-foreground-500">
            <span className="flex items-center gap-1.5">
              <i className="ri-film-line text-base text-primary-500" />
              共 {results.length} 部作品
            </span>
            {keyword && (
              <span className="flex items-center gap-1.5 rounded-full bg-accent-100 px-3 py-1 text-accent-900">
                关键词：{keyword}
              </span>
            )}
          </div>
        </div>

        <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-12">
          <div className="lg:col-span-9">
            <div
              className={[
                "rounded-lg border border-background-200 bg-background-50 px-4 py-2 md:px-5",
                filterOpen ? "block" : "hidden lg:block",
              ].join(" ")}
            >
              <div className={filterRowClass}>
                <span className="w-16 shrink-0 text-xs text-foreground-500">类型</span>
                <div className="flex flex-wrap gap-2">
                  {typeOptions.map((option) => (
                    <button
                      key={option}
                      type="button"
                      onClick={() => updateParam("type", option)}
                      className={chipClass(effectiveType === option)}
                    >
                      {option}
                    </button>
                  ))}
                </div>
              </div>
              <div className={filterRowClass}>
                <span className="w-16 shrink-0 text-xs text-foreground-500">地区</span>
                <div className="flex flex-wrap gap-2">
                  {regionOptions.map((option) => (
                    <button
                      key={option}
                      type="button"
                      onClick={() => updateParam("region", option)}
                      className={chipClass(effectiveRegion === option)}
                    >
                      {option}
                    </button>
                  ))}
                </div>
              </div>
              <div className={filterRowClass}>
                <span className="w-16 shrink-0 text-xs text-foreground-500">年份</span>
                <div className="flex flex-wrap gap-2">
                  {yearFilters.map((option) => (
                    <button
                      key={option}
                      type="button"
                      onClick={() => updateParam("year", option)}
                      className={chipClass((yearParam || "全部") === option)}
                    >
                      {option}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <div className="mt-5 flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setFilterOpen((open) => !open)}
                  className="flex cursor-pointer items-center gap-1.5 rounded-md border border-background-300 px-3 py-2 text-xs text-foreground-700 lg:hidden"
                >
                  <i className="ri-filter-3-line text-base" />
                  筛选
                </button>
                {hasFilters && (
                  <button
                    type="button"
                    onClick={resetAll}
                    className="flex cursor-pointer items-center gap-1 whitespace-nowrap rounded-md border border-background-300 px-3 py-2 text-xs text-foreground-600 transition-colors hover:border-accent-400 hover:text-accent-700"
                  >
                    <i className="ri-refresh-line text-sm" />
                    重置筛选
                  </button>
                )}
              </div>

              <div className="flex flex-wrap items-center gap-2">
                <span className="text-xs text-foreground-500">排序</span>
                <div className="flex items-center gap-1 rounded-full border border-background-300 bg-background-50 p-1">
                  {sortOptions.map((option) => (
                    <button
                      key={option.key}
                      type="button"
                      onClick={() => updateParam("sort", option.key === "updated" ? "" : option.key)}
                      className={[
                        "cursor-pointer whitespace-nowrap rounded-full px-3 py-1.5 text-xs transition-colors",
                        effectiveSort === option.key
                          ? "bg-primary-500 text-background-50"
                          : "text-foreground-600 hover:text-primary-600",
                      ].join(" ")}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {visible.length === 0 ? (
              <div className="mt-6">
                <EmptyState
                  title="未找到符合条件的影视作品"
                  description="试试更换类型、地区或年份，或者清除关键词后重新浏览全部作品。"
                  actionLabel="重置筛选"
                  onAction={resetAll}
                />
              </div>
            ) : (
              <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
                {visible.map((movie) => (
                  <MovieCard key={movie.id} movie={movie} />
                ))}
              </div>
            )}

            <Pagination page={currentPage} totalPages={totalPages} onChange={goPage} />
          </div>

          <aside className="hidden lg:col-span-3 lg:block">
            <div className="flex flex-col gap-6 lg:sticky lg:top-24">
              <div className="rounded-lg border border-background-200 bg-background-50 p-4 md:p-5">
                <h2 className="text-sm font-bold text-foreground-950">筛选说明</h2>
                <ul className="mt-3 space-y-2.5 text-xs leading-6 text-foreground-600">
                  <li className="flex gap-2">
                    <i className="ri-check-line mt-1 text-accent-600" />
                    类型区分电影与电视剧，可单独浏览。
                  </li>
                  <li className="flex gap-2">
                    <i className="ri-check-line mt-1 text-accent-600" />
                    年份筛选包含 2023 年及更早作品。
                  </li>
                  <li className="flex gap-2">
                    <i className="ri-check-line mt-1 text-accent-600" />
                    排序会实时刷新结果，不影响筛选条件。
                  </li>
                </ul>
                <button
                  type="button"
                  onClick={resetAll}
                  className="mt-4 w-full cursor-pointer whitespace-nowrap rounded-md bg-secondary-100 px-3 py-2 text-xs font-medium text-secondary-900 transition-colors hover:bg-secondary-200"
                >
                  查看全部作品
                </button>
              </div>

              <div className="rounded-lg border border-background-200 bg-background-50 p-4 md:p-5">
                <h2 className="text-sm font-bold text-foreground-950">热门标签</h2>
                <div className="mt-3 flex flex-wrap gap-2">
                  {["悬疑", "家庭", "剧情", "科幻", "纪录", "温情", "律政"].map((genre) => (
                    <button
                      key={genre}
                      type="button"
                      onClick={() => {
                        const next = new URLSearchParams();
                        next.set("q", genre);
                        setSearchParams(next);
                      }}
                      className="cursor-pointer rounded-full bg-background-100 px-2.5 py-1 text-[11px] text-foreground-600 transition-colors hover:bg-accent-100 hover:text-accent-900"
                    >
                      #{genre}
                    </button>
                  ))}
                </div>
              </div>

              <div className="rounded-lg bg-primary-950 p-4 text-background-50 md:p-5">
                <p className="font-label text-[10px] tracking-[0.24em] text-primary-400">COLLECTION</p>
                <h2 className="mt-2 text-sm font-bold">找不到想看的？</h2>
                <p className="mt-2 text-xs leading-6 text-primary-200">
                  在顶部搜索框输入片名、主演或关键词，我们会在全站影视资料中检索。
                </p>
              </div>
            </div>
          </aside>
        </div>
      </main>
      <SiteFooter />
    </div>
  );
}